import { ChevronDown, ChevronUp, ChevronsUpDown, Eye } from 'lucide-react';
import ApprovalStatusBadge from '../../components/common/ApprovalStatusBadge';
import type {
  SkillApprovalRow,
  SkillApprovalSortColumn,
  SkillApprovalSortDirection,
} from '../../types/admin';

interface SkillApprovalsTableProps {
  rows: SkillApprovalRow[];
  loading: boolean;
  actionLoadingKey: string | null;
  sortColumn: SkillApprovalSortColumn;
  sortDirection: SkillApprovalSortDirection;
  onSort: (column: SkillApprovalSortColumn) => void;
  onView: (row: SkillApprovalRow) => void;
  onApprove: (row: SkillApprovalRow) => void;
  onReject: (row: SkillApprovalRow) => void;
}

const formatDate = (value?: string | null) => {
  if (!value) return 'Not provided';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not provided';

  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

function SortIcon({ active, direction }: { active: boolean; direction: SkillApprovalSortDirection }) {
  if (!active) return <ChevronsUpDown className="h-3.5 w-3.5" />;
  return direction === 'Ascending' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />;
}

function SortHeader({
  label,
  column,
  sortColumn,
  sortDirection,
  onSort,
}: {
  label: string;
  column: SkillApprovalSortColumn;
  sortColumn: SkillApprovalSortColumn;
  sortDirection: SkillApprovalSortDirection;
  onSort: (column: SkillApprovalSortColumn) => void;
}) {
  const active = sortColumn === column;

  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      className="inline-flex items-center gap-1 text-xs font-semibold uppercase text-gray-500"
    >
      {label}
      <SortIcon active={active} direction={sortDirection} />
    </button>
  );
}

function EmployeeCell({ row }: { row: SkillApprovalRow }) {
  return (
    <span className="flex min-w-0 items-center gap-3 text-left">
      <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-primary text-sm font-semibold text-white">
        {getInitials(row.employeeName)}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-gray-900">{row.employeeName}</span>
        {row.email && <span className="block truncate text-xs text-gray-500">{row.email}</span>}
      </span>
    </span>
  );
}

function TableSkeleton() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, index) => (
        <tr key={index} className="animate-pulse">
          {Array.from({ length: 6 }).map((__, cellIndex) => (
            <td key={cellIndex} className="px-4 py-4">
              <div className="h-4 rounded bg-gray-100" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export default function SkillApprovalsTable({
  rows,
  loading,
  actionLoadingKey,
  sortColumn,
  sortDirection,
  onSort,
  onView,
  onApprove,
  onReject,
}: SkillApprovalsTableProps) {
  return (
    <>
      <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:block">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <SortHeader label="Employee" column="EmployeeName" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
              </th>
              <th className="px-4 py-3 text-left">
                <SortHeader label="Skill" column="SkillName" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Experience (yrs)</th>
              <th className="px-4 py-3 text-left">
                <SortHeader label="Submitted Date" column="SubmittedDate" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
              </th>
              <th className="px-4 py-3 text-left">
                <SortHeader label="Status" column="ApprovalStatus" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <TableSkeleton />
            ) : (
              rows.map((row) => {
                const actionKey = String(row.userSkillId);
                const isProcessing = actionLoadingKey === actionKey;
                const isPending = String(row.approvalStatus || '').trim().toLowerCase() === 'pending';

                return (
                  <tr key={row.userSkillId} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <EmployeeCell row={row} />
                    </td>
                    <td className="px-4 py-4 text-sm font-semibold text-gray-900">{row.skillName}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{row.experience}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{formatDate(row.submittedDate)}</td>
                    <td className="px-4 py-4">
                      <ApprovalStatusBadge status={row.approvalStatus} />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => onView(row)}
                          className="inline-flex h-9 items-center justify-center rounded-lg border border-gray-200 px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                        >
                          <Eye className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                          View
                        </button>
                        {isPending && (
                          <>
                            <button
                              type="button"
                              onClick={() => onReject(row)}
                              disabled={isProcessing}
                              className="inline-flex h-9 items-center justify-center rounded-lg bg-red-600 px-3 text-xs font-semibold text-white disabled:bg-gray-300"
                            >
                              Reject
                            </button>
                            <button
                              type="button"
                              onClick={() => onApprove(row)}
                              disabled={isProcessing}
                              className="inline-flex h-9 items-center justify-center rounded-lg bg-brand-primary px-3 text-xs font-semibold text-white disabled:bg-gray-300"
                            >
                              {isProcessing ? 'Processing...' : 'Approve'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="space-y-3 md:hidden">
        {loading ? (
          Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="animate-pulse rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="mb-4 h-4 w-36 rounded bg-gray-200" />
              <div className="mb-3 h-3 w-28 rounded bg-gray-100" />
              <div className="h-3 w-44 rounded bg-gray-100" />
            </div>
          ))
        ) : (
          rows.map((row) => {
            const actionKey = String(row.userSkillId);
            const isProcessing = actionLoadingKey === actionKey;
            const isPending = String(row.approvalStatus || '').trim().toLowerCase() === 'pending';

            return (
              <article key={row.userSkillId} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-start justify-between gap-3">
                  <EmployeeCell row={row} />
                  <ApprovalStatusBadge status={row.approvalStatus} />
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
                  <p><span className="font-semibold text-gray-900">{row.skillName}</span></p>
                  <p>{row.experience} yrs experience</p>
                  <p className="col-span-2">Submitted: {formatDate(row.submittedDate)}</p>
                </div>
                <div className={`mt-4 grid gap-2 ${isPending ? 'grid-cols-3' : 'grid-cols-1'}`}>
                  <button type="button" onClick={() => onView(row)} className="inline-flex h-10 items-center justify-center rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                    <Eye className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> View
                  </button>
                  {isPending && (
                    <>
                      <button type="button" onClick={() => onReject(row)} disabled={isProcessing} className="h-10 rounded-lg bg-red-600 text-xs font-semibold text-white disabled:bg-gray-300">
                        Reject
                      </button>
                      <button type="button" onClick={() => onApprove(row)} disabled={isProcessing} className="h-10 rounded-lg bg-brand-primary text-xs font-semibold text-white disabled:bg-gray-300">
                        Approve
                      </button>
                    </>
                  )}
                </div>
              </article>
            );
          })
        )}
      </div>
    </>
  );
}
